import { useCallback, useEffect, useState } from "react";
import { type Dataset, listDatasets } from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset/loadState";

/** 최근 등록 카드가 보여 주는 데이터셋 수. */
export const RECENT_DATASET_LIMIT = 5;

export type RecentDatasetsController = {
  state: LoadState<readonly Dataset[]>;
  reload: () => void;
};

/**
 * 최근 등록 데이터셋을 로드한다.
 *
 * 목록 API 의 서버 정렬은 생성 내림차순으로 고정되어 있다(sort 파라미터 없음). 그래서 첫 페이지가
 * 곧 최신 목록이다. 모집단은 대시보드의 다른 카드와 같은 available 이다.
 *
 * `useDatasetAnalytics` 와 같은 골격이지만 버킷 캐시는 없다. 다시 조회(새로고침, 섬네일 만료)하는
 * 동안에도 직전 목록을 유지해 카드가 깜빡이지 않게 한다.
 */
export function useRecentDatasets(): RecentDatasetsController {
  const [state, setState] = useState<LoadState<readonly Dataset[]>>({ status: "idle" });
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setState((previous) => (previous.status === "success" ? previous : { status: "loading" }));
      try {
        const page = await listDatasets({
          page: 1,
          size: RECENT_DATASET_LIMIT,
          status: "available",
        });
        if (cancelled) {
          return;
        }
        setState({ status: "success", data: page.items });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setState({ status: "error", message: resolveLoadErrorMessage(error) });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  return { state, reload };
}
